import { useEffect, useRef } from 'react';

import type { TableHeaderProps } from '../types';

interface UseColumnResizeIndicatorOptions {
  header?: TableHeaderProps;
  onResizeEnd?: (width: number) => void;
  setResizableWidth: (width: number) => void;
}

interface UseColumnResizeIndicatorResult {
  isResizing: boolean;
  resizeDelta: number;
}

/**
 * Hook to manage column resize visual indicator state.
 * Tracks when resizing starts/ends and calculates the resize delta for visual indicators.
 */
export const useColumnResizeIndicator = (options: UseColumnResizeIndicatorOptions): UseColumnResizeIndicatorResult => {
  const { header, onResizeEnd, setResizableWidth } = options;

  const isResizing = header?.column.getIsResizing() ?? false;
  const wasResizingRef = useRef(false);
  const initialWidthRef = useRef<number>(0);

  // Store initial width when resize starts
  useEffect(() => {
    if (isResizing && !wasResizingRef.current && header) {
      initialWidthRef.current = header.getSize();
    }
  }, [isResizing, header]);

  // Detect when resizing ends and call onResizeEnd with the new width
  useEffect(() => {
    if (wasResizingRef.current && !isResizing && header) {
      const newWidth = header.getSize();

      setResizableWidth(newWidth);
      onResizeEnd?.(newWidth);
    }
    wasResizingRef.current = isResizing;
  }, [isResizing, header, onResizeEnd, setResizableWidth]);

  // Calculate resize delta for positioning the indicator line
  const resizeDelta = isResizing && header ? header.getSize() - initialWidthRef.current : 0;

  return {
    isResizing,
    resizeDelta
  };
};
